import React from "react";
import { View, StyleSheet, TouchableOpacity, ViewStyle, StyleProp, TextStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { router, useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { Button, ButtonProps } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface PreviewSectionProps {
  title: string;
  children: React.ReactNode;
  style?: any;
  headerStyle?: ViewStyle;
  button?: ButtonProps;
  editing?: boolean;
  badge?: React.ReactNode;
  /** Custom style for the title/label */
  titleStyle?: StyleProp<TextStyle>;
}

export function PreviewSection({
  title,
  children,
  style,
  button,
  editing,
  headerStyle,
  badge,
  titleStyle,
}: PreviewSectionProps) {
  const { colors } = useTheme();

  return (
    <View style={[styles.section, style]}>
      <View style={[{ flex: 1, flexDirection: "row", alignItems: "center", gap: 8 }, headerStyle]}>
        <ThemedText type="defaultSemiBold" style={[styles.sectionTitle, titleStyle]}>
          {title}
        </ThemedText>
        {badge}
        <View style={{ flex: 1 }} />
        {editing && button && <Button style={{ gap: 8, backgroundColor: colors.primary }} {...button} />}
      </View>
      {children}
    </View>
  );
}

export interface InfoRowProps {
  label: string;
  value: string;
  params?: any;
}

export function InfoRow({ label, value, params }: InfoRowProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();

  const handleNavigation = () => {
    if (!params) return;

    // Save current entity to history before navigating
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;

    if (currentPanel && currentId) {
      pushNavigation({
        panel: currentPanel,
        id: currentId,
        // Use context title if available (from PreviewHeader), otherwise fall back to value
        title: currentEntity?.currentTitle || value,
      });
    }

    // Navigate to new entity
    router.setParams(params);
  };

  return (
    <View style={styles.infoRow}>
      <ThemedText style={{ opacity: 0.6 }}>{label}</ThemedText>
      {params ? (
        <TouchableOpacity onPress={handleNavigation}>
          <ThemedText type="link">{value}</ThemedText>
        </TouchableOpacity>
      ) : (
        <ThemedText>{value}</ThemedText>
      )}
    </View>
  );
}

export interface ToggleButtonRowProps {
  label: string;
  value: boolean;
  onChange?: (value: boolean) => void;
  editing?: boolean;
}

export function ToggleButtonRow({ label, value, onChange, editing = false }: ToggleButtonRowProps) {
  const { colors } = useTheme();
  const isEnabled = value;

  return (
    <View style={styles.toggleRow}>
      <ThemedText style={{ opacity: 0.6, flex: 1 }}>{label}</ThemedText>
      {editing ? (
        <TouchableOpacity
          style={[
            styles.toggleSwitch,
            {
              backgroundColor: isEnabled ? colors.primary : colors.border,
            },
          ]}
          onPress={() => onChange?.(!isEnabled)}
        >
          <View
            style={[
              styles.toggleThumb,
              {
                backgroundColor: colors.card,
                transform: [{ translateX: isEnabled ? 20 : 2 }],
              },
            ]}
          />
        </TouchableOpacity>
      ) : (
        <ThemedText style={{ fontWeight: "500" }}>{value ? "Yes" : "No"}</ThemedText>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: 8,
  },
  sectionTitle: {
    marginBottom: 4,
    minWidth: 100,
  },
  infoRow: {
    justifyContent: "space-between",
    gap: 4,
    paddingVertical: 4,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    padding: 2,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: "0 2px 2.22px 0 rgba(0, 0, 0, 0.22)",
    elevation: 3,
  },
});
